// Hedef Termometresi — dikey gauge.
// Task 14: payda artık `effective_goal_tl = hedef_ev + likidite_tamponu`.
// Üst etiket "X TL · ev+yastık" + alt satırda breakdown "ev 35M + yastık 13M".
// effectiveGoalTl yoksa (cold start / API hata) "—" göster.

interface Props {
  /** 0..100 arası dolum yüzdesi (goal_ratio_effective × 100). */
  pct: number;
  /** Hedef toplamı (ev + likidite). null ise "—" göster. */
  effectiveGoalTl?: number | null;
  /** Sadece ev hedefi (breakdown alt-satırı için). */
  evGoalTl?: number | null;
  /** Likidite tamponu hedefi (breakdown alt-satırı için). */
  reserveTl?: number | null;
  /** Tahmini varış (örn. "Mar 2031") */
  arrival?: string;
}

function fmtHedefShort(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "— TL";
  if (n >= 1_000_000) {
    const v = (n / 1_000_000).toFixed(1).replace(".", ",");
    return `${v}M TL`;
  }
  return `${Math.round(n).toLocaleString("tr-TR")} TL`;
}

export default function Termometre({
  pct,
  effectiveGoalTl = null,
  evGoalTl = null,
  reserveTl = null,
  arrival = "Mar 2031",
}: Props) {
  const clamped = Math.max(0, Math.min(100, pct));
  const display = Math.round(clamped);
  const hedefLabel = fmtHedefShort(effectiveGoalTl);
  const hasBreakdown =
    evGoalTl !== null &&
    evGoalTl !== undefined &&
    reserveTl !== null &&
    reserveTl !== undefined &&
    reserveTl > 0;

  return (
    <article
      className="w-[120px] shrink-0 rounded-2xl bg-ink-800 border border-ink-700 p-3 card-shadow flex flex-col"
      aria-label="Hedef ilerleme termometresi"
    >
      <header className="text-[11px] uppercase tracking-wider text-ink-400 font-medium">
        Hedef
      </header>
      <p className="text-ink-100 text-sm mt-0.5">
        {hedefLabel}
        <span className="text-ink-400"> · ev+yastık</span>
      </p>
      {hasBreakdown && (
        <p
          className="text-[10px] text-ink-400 leading-tight mt-0.5"
          title="Hedef ev fiyatı + eve girince elde kalması gereken likidite yastığı"
        >
          ev {fmtHedefShort(evGoalTl)} + yastık {fmtHedefShort(reserveTl)}
        </p>
      )}

      {/* Gauge gövdesi. Yükseklik ebeveynin (`.flex-1`) kalan alanını alır;
          böylece Çevirmen Şeridi ile aynı yüksekliğe oturduğunda termometre
          de uzar. Min/max ölçek görsel tutarlılık için sınırlı. */}
      <div
        className="relative mt-3 mx-auto flex-1"
        style={{ width: 28, minHeight: 200, maxHeight: 340 }}
        aria-hidden="true"
      >
        {/* Boş tüp */}
        <div className="absolute inset-0 rounded-full bg-ink-700" />
        {/* Dolu kısım (alttan başlar, %clamped yüksekliğine kadar uzar) */}
        <div
          className="absolute left-0 right-0 bottom-0 rounded-full bg-accent-500 fill-anim"
          style={{ height: `${clamped}%` }}
        />
        {/* Uç-yuvarlağı: dolum üst seviyesinde otursun (terazi göstergesi).
            18px = yuvarlak yarıçapı; `bottom: calc(pct% - 18px)` ile merkez
            tam doluluk noktasında konumlanır. clamp() ile uçlardan taşmayı engelle. */}
        <div
          className="absolute left-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-accent-500 border-2 border-ink-900 transition-[bottom] duration-500"
          style={{
            bottom: `calc(clamp(0%, ${clamped}%, 100%) - 18px)`,
          }}
        />
      </div>

      <div className="mt-3 text-center">
        <p className="fon-dili text-accent-300 text-xl font-semibold">
          %{display}
        </p>
        <p className="text-[11px] text-ink-300 mt-0.5">dolu</p>
        <p className="hedef-dili text-ink-100 text-sm mt-2 leading-snug">
          varış
          <br />
          <span className="text-accent-300 font-medium not-italic">
            {arrival}
          </span>
        </p>
      </div>
    </article>
  );
}
